/**
 * @module components/HudMissionTracker
 * @description "Mission-control" HUD banner displayed at the very top of the
 * Dashboard — Yuri spec 2026-02 Clean & Glow.
 *
 * Iteration 315 (Cycle Companion Mode): when the user is on an active
 * menstrual cycle and currently in luteal/pre_menstrual/menstrual phase, the
 * HUD switches to a "récupération" mode:
 *   - Workout pillar becomes optional (counts as bonus when hit, never blocks)
 *   - Step goal effective is reduced by 10% to honor the physiological reality
 *   - Border + LED accents shift from orange/emerald to mauve/fuchsia
 *   - Label changes from "X PILIERS PRIORITAIRES" to "MODE RÉCUP · X PILIERS"
 *   - Discreet phase chip on the right ("🌸 Lutéale")
 *
 * This is the only women-aware feature in the global HUD. Backend remains
 * untouched — the gating is purely presentational and uses /api/users/cycle-info
 * which already exists since iteration 312.
 *
 * Data contract (stats, coming from Dashboard.js):
 *   - today_steps / step_goal
 *   - today_protein_g / protein_target_min
 *   - has_workout_today
 *   - current_streak OR active_days (optional — used to render Mission #N)
 */
import React, { useEffect, useState } from 'react';
import { Radio, Activity, Sparkles } from 'lucide-react';
import { api } from '../context/AppContext';

const COMPANION_PHASES = new Set(['luteal', 'pre_menstrual', 'menstrual']);
const COMPANION_STEP_FACTOR = 0.9; // -10% target during recovery phases

// Display label for each phase (FR primary — keep concise, single token).
const PHASE_LABEL_FR = {
  menstrual: 'Menstruelle',
  follicular: 'Folliculaire',
  ovulation: 'Ovulation',
  luteal: 'Lutéale',
  pre_menstrual: 'Pré-règles',
};
const PHASE_LABEL_EN = {
  menstrual: 'Menstrual',
  follicular: 'Follicular',
  ovulation: 'Ovulation',
  luteal: 'Luteal',
  pre_menstrual: 'Pre-period',
};

function pad(n) {
  return n < 10 ? `0${n}` : `${n}`;
}

export default function HudMissionTracker({ stats, language = 'fr' }) {
  const fr = language === 'fr';
  const [now, setNow] = useState(() => new Date());

  // Cycle phase — fetched lazily from /api/users/cycle-info. Returns null when
  // the user isn't on an active cycle (or not female), so the HUD stays in its
  // default "3 piliers prioritaires" mode.
  const [cyclePhase, setCyclePhase] = useState(null);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000); // update every 30s
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let alive = true;
    api.get('/users/cycle-info')
      .then((r) => {
        if (!alive) return;
        // Only react when the cycle is configured and active.
        if (r?.data?.active && r?.data?.phase) {
          setCyclePhase(r.data.phase);
        }
      })
      .catch(() => { /* silent — non-female users get 200 with active:false */ });
    return () => { alive = false; };
  }, []);

  const timeLabel = `${pad(now.getHours())}:${pad(now.getMinutes())}`;

  // Mission number — use streak / active-days / default 1
  const missionNumber =
    Number(stats?.current_streak) ||
    Number(stats?.active_days) ||
    Number(stats?.days_logged) ||
    1;

  const steps = Number(stats?.today_steps) || 0;
  const stepGoalRaw = Number(stats?.step_goal) || 10000;
  const protein = Number(stats?.today_protein_g) || 0;
  const proteinTarget = Number(stats?.protein_target_min) || 100;
  const hasWorkout = !!stats?.has_workout_today;

  // 🌸 Companion mode trigger
  const companion = cyclePhase && COMPANION_PHASES.has(cyclePhase);
  const stepGoal = companion ? Math.round(stepGoalRaw * COMPANION_STEP_FACTOR) : stepGoalRaw;

  const stepsHit = steps >= stepGoal;
  const proteinHit = protein >= proteinTarget;
  const workoutHit = hasWorkout;

  // In companion mode the workout pillar is optional → not counted in remaining.
  const remaining = companion
    ? [stepsHit, proteinHit].filter((v) => !v).length
    : [stepsHit, proteinHit, workoutHit].filter((v) => !v).length;
  const totalPillars = companion ? 2 : 3;
  const allDone = remaining === 0;

  // Theme palette — mauve/fuchsia in companion, orange/amber otherwise.
  const theme = companion
    ? {
        borderColor: allDone ? 'rgba(232,121,249,0.55)' : 'rgba(192,132,252,0.50)',
        shadow: allDone
          ? '0 0 18px rgba(232,121,249,0.40), inset 0 0 0 1px rgba(232,121,249,0.15)'
          : '0 0 18px rgba(192,132,252,0.35), inset 0 0 0 1px rgba(192,132,252,0.12)',
        scanRgba: 'rgba(192,132,252,0.06)',
        clockColor: 'text-fuchsia-300',
        clockShadow: '0 0 8px rgba(232,121,249,0.55)',
        sepColor: 'text-fuchsia-500/50',
        missionColor: 'text-fuchsia-200',
        missionShadow: '0 0 8px rgba(232,121,249,0.55)',
        ledLive: 'bg-fuchsia-400',
        ledLiveShadow: '0 0 8px rgba(232,121,249,0.95)',
        ledDone: 'bg-amber-400',
        ledDoneShadow: '0 0 8px rgba(251,191,36,0.95)',
        priorityColor: allDone ? 'text-amber-300' : 'text-fuchsia-200',
        priorityShadow: allDone ? '0 0 8px rgba(251,191,36,0.45)' : '0 0 8px rgba(232,121,249,0.45)',
        radioColor: 'text-fuchsia-400/80',
      }
    : {
        borderColor: allDone ? 'rgba(251,191,36,0.45)' : 'rgba(251,146,60,0.40)',
        shadow: allDone
          ? '0 0 18px rgba(251,191,36,0.30), inset 0 0 0 1px rgba(251,191,36,0.10)'
          : '0 0 18px rgba(251,146,60,0.25), inset 0 0 0 1px rgba(251,146,60,0.10)',
        scanRgba: 'rgba(251,146,60,0.045)',
        clockColor: 'text-orange-300',
        clockShadow: '0 0 8px rgba(251,146,60,0.55)',
        sepColor: 'text-orange-500/50',
        missionColor: 'text-amber-300',
        missionShadow: '0 0 8px rgba(251,191,36,0.55)',
        ledLive: 'bg-emerald-400',
        ledLiveShadow: '0 0 8px rgba(52,211,153,0.95)',
        ledDone: 'bg-amber-400',
        ledDoneShadow: '0 0 8px rgba(251,191,36,0.95)',
        priorityColor: allDone ? 'text-amber-300' : 'text-emerald-300',
        priorityShadow: allDone ? '0 0 8px rgba(251,191,36,0.45)' : '0 0 8px rgba(52,211,153,0.45)',
        radioColor: 'text-orange-400/80',
      };

  // Priority text — companion mode reads "MODE RÉCUP · N PILIER(S)" instead of the
  // standard prioritaire wording, to make the shift unmistakable.
  let priorityText;
  if (companion) {
    priorityText = fr
      ? (allDone
          ? 'Mission accomplie · Phase récup honorée'
          : `Mode récup · ${remaining} pilier${remaining > 1 ? 's' : ''}`)
      : (allDone ? 'Mission complete · Recovery honored' : `Recovery mode · ${remaining} pillar${remaining > 1 ? 's' : ''}`);
  } else {
    priorityText = fr
      ? (allDone
          ? 'Mission accomplie · Victoire propre'
          : `${remaining} pilier${remaining > 1 ? 's' : ''} prioritaire${remaining > 1 ? 's' : ''}`)
      : (allDone ? 'Mission complete · Clean win' : `${remaining} priority pillar${remaining > 1 ? 's' : ''}`);
  }

  const phaseChipLabel =
    companion && cyclePhase
      ? (fr ? PHASE_LABEL_FR[cyclePhase] : PHASE_LABEL_EN[cyclePhase])
      : null;

  return (
    <div
      className="mb-4 rounded-xl overflow-hidden relative"
      data-testid="hud-mission-tracker"
      data-companion-mode={companion ? '1' : '0'}
      style={{
        background:
          'linear-gradient(90deg, rgba(10,10,10,0.96) 0%, rgba(15,15,20,0.96) 40%, rgba(10,10,10,0.96) 100%)',
        border: `1px solid ${theme.borderColor}`,
        boxShadow: theme.shadow,
      }}
    >
      {/* subtle scan-line texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background: `repeating-linear-gradient(0deg, transparent 0px, transparent 3px, ${theme.scanRgba} 3px, ${theme.scanRgba} 4px)`,
        }}
      />

      <div className="relative flex items-center gap-3 px-3.5 py-2 font-mono">
        {/* LED */}
        <span className="relative flex h-2.5 w-2.5 flex-shrink-0" aria-hidden="true">
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${
              allDone ? theme.ledDone : theme.ledLive
            }`}
          />
          <span
            className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
              allDone ? theme.ledDone : theme.ledLive
            }`}
            style={{ boxShadow: allDone ? theme.ledDoneShadow : theme.ledLiveShadow }}
          />
        </span>

        {/* Clock */}
        <Radio className={`w-3.5 h-3.5 ${theme.radioColor} flex-shrink-0`} />
        <span
          className={`text-[12px] sm:text-sm font-black tabular-nums tracking-widest ${theme.clockColor}`}
          data-testid="hud-mission-time"
          style={{ textShadow: theme.clockShadow }}
        >
          {timeLabel}
        </span>

        <span className={`${theme.sepColor} select-none`}>·</span>

        {/* Mission number */}
        <span
          className={`text-[11px] sm:text-[12px] font-black uppercase tracking-[0.22em] ${theme.missionColor}`}
          data-testid="hud-mission-number"
          style={{ textShadow: theme.missionShadow }}
        >
          {fr ? 'Mission' : 'Mission'} #{pad(missionNumber)}
        </span>

        <span className={`${theme.sepColor} select-none hidden sm:inline`}>·</span>

        {/* Priority pillars */}
        <span
          className={`hidden sm:inline-flex items-center gap-1.5 text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.18em] ${theme.priorityColor}`}
          data-testid="hud-mission-priority"
          style={{ textShadow: theme.priorityShadow }}
        >
          {companion ? <Sparkles className="w-3 h-3" /> : <Activity className="w-3 h-3" />}
          {priorityText}
        </span>

        {/* Companion mode phase chip — desktop only, sits on the right */}
        {phaseChipLabel && (
          <span
            data-testid="hud-mission-phase-chip"
            className="hidden sm:inline-flex items-center gap-1.5 ml-auto px-2 py-0.5 rounded-full text-[9px] font-black tracking-[0.22em] uppercase text-fuchsia-200"
            style={{
              background: 'rgba(192,132,252,0.10)',
              border: '1px solid rgba(232,121,249,0.40)',
              boxShadow: '0 0 8px rgba(192,132,252,0.35)',
            }}
            title={fr ? `Phase ${phaseChipLabel.toLowerCase()} · ton corps consomme +5-10% d'énergie, l'app s'adapte` : `${phaseChipLabel} phase · adapted UI`}
          >
            🌸 {phaseChipLabel}
          </span>
        )}

        {/* mobile version → compact chip on the right.
            Reads validated/total ("0/3"), with ✓ when complete. */}
        <span
          className={`sm:hidden ml-auto text-[10px] font-black uppercase tracking-widest ${theme.priorityColor}`}
          data-testid="hud-mission-priority-mobile"
        >
          {allDone ? (fr ? '✓ Clean' : '✓ Clean') : `${totalPillars - remaining}/${totalPillars}`}
        </span>
      </div>
    </div>
  );
}
